// Pure report maths (architecture sprint-04 §6.3). Presets, densified days, weekly buckets by
// project id (never by name, INV #15) and the heatmap grid.
import { addDays, datesInRange, diffDays, isValidLocalDate, monthRange, weekStart, type LocalDate } from '@shared/calendar';
import { NO_PROJECT_LABEL } from '@shared/palette';
import { HEATMAP_BUCKET_BOUNDS, HEATMAP_DAYS, MAX_REPORT_RANGE_DAYS, REPORT_PRESET_DAYS } from '@shared/report-rules';
import { formatDayMonth } from '@/lib/format';
import type { Project } from '@/types/projects';
import type { DailyTotalRow } from '@/types/reports';

const SECONDS_PER_HOUR = 3600;
const DAYS_PER_WEEK = 7;

export type PresetKey = '7d' | '30d' | '3m' | '12m' | 'month' | 'last-month';

export const PRESETS: Array<{ key: PresetKey; label: string }> = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '3m', label: '3 months' },
  { key: '12m', label: '12 months' },
  { key: 'month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
];

export interface DateRange {
  from: LocalDate;
  to: LocalDate;
}

export function presetRange(key: PresetKey, today: LocalDate): DateRange {
  if (key === 'month') return { from: monthRange(today).start, to: today };
  if (key === 'last-month') {
    const last = monthRange(addDays(monthRange(today).start, -1));
    return { from: last.start, to: last.end };
  }
  return { from: addDays(today, 1 - REPORT_PRESET_DAYS[key]), to: today };
}

/** A usable report range: valid dates, ordered, at most 366 days inclusive. */
export function isValidReportRange(from: string | null, to: string | null): boolean {
  if (!from || !to || !isValidLocalDate(from) || !isValidLocalDate(to)) return false;
  return to >= from && diffDays(to, from) < MAX_REPORT_RANGE_DAYS;
}

export function heatmapRange(today: LocalDate): DateRange {
  return { from: addDays(today, 1 - HEATMAP_DAYS), to: today };
}

export interface DayPoint {
  date: LocalDate;
  seconds: number;
  hours: number;
}

/** Every day in [from, to], zeros included (DST days are ordinary dates). */
export function densifyDays(rows: DailyTotalRow[], from: LocalDate, to: LocalDate): DayPoint[] {
  const byDate = new Map<LocalDate, number>();
  for (const r of rows) byDate.set(r.date, (byDate.get(r.date) ?? 0) + r.seconds);
  return datesInRange(from, to).map((date) => {
    const seconds = byDate.get(date) ?? 0;
    return { date, seconds, hours: seconds / SECONDS_PER_HOUR };
  });
}

export interface WeekSeries {
  key: string;
  label: string;
  color: string | null;
  archived: boolean;
}

export interface WeekBucket {
  weekStart: LocalDate;
  label: string;
  __seconds: Record<string, number>;
  [key: string]: number | string | Record<string, number>;
}

/** Mon–Sun buckets from weekStart(from) to weekStart(to); only days inside [from, to] count. */
export function weeklyByProject(rows: DailyTotalRow[], from: LocalDate, to: LocalDate, projects: Project[]): { weeks: WeekBucket[]; series: WeekSeries[] } {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const weeks: WeekBucket[] = [];
  const index = new Map<LocalDate, WeekBucket>();
  for (let w = weekStart(from); w <= weekStart(to); w = addDays(w, DAYS_PER_WEEK)) {
    const bucket: WeekBucket = { weekStart: w, label: `w/c ${formatDayMonth(w)}`, __seconds: {} };
    weeks.push(bucket);
    index.set(w, bucket);
  }

  const totals = new Map<string, number>();
  for (const r of rows) {
    if (r.date < from || r.date > to || r.seconds <= 0) continue;
    const bucket = index.get(weekStart(r.date));
    if (!bucket) continue;
    const key = r.projectId ?? 'none';
    bucket.__seconds[key] = (bucket.__seconds[key] ?? 0) + r.seconds;
    totals.set(key, (totals.get(key) ?? 0) + r.seconds);
  }
  for (const bucket of weeks) {
    for (const [key, seconds] of Object.entries(bucket.__seconds)) bucket[key] = seconds / SECONDS_PER_HOUR;
  }

  const series: WeekSeries[] = [...totals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([key]) => {
      const p = key === 'none' ? undefined : byId.get(key);
      return { key, label: p?.name ?? NO_PROJECT_LABEL, color: p?.color ?? null, archived: p?.status === 'archived' };
    });
  return { weeks, series };
}

export type HeatBucket = 0 | 1 | 2 | 3 | 4;

export function heatBucket(seconds: number): HeatBucket {
  if (seconds <= 0) return 0;
  const [b1, b2, b3] = HEATMAP_BUCKET_BOUNDS;
  if (seconds < b1) return 1;
  if (seconds < b2) return 2;
  if (seconds < b3) return 3;
  return 4;
}

export interface HeatCell {
  date: LocalDate;
  seconds: number;
  bucket: HeatBucket;
  inWindow: boolean;
}

export interface HeatWeek {
  weekStart: LocalDate;
  cells: HeatCell[];
}

export interface HeatmapGrid {
  weeks: HeatWeek[];
  /** Column index → month label, for the first week column of each month. */
  monthLabels: Array<{ index: number; label: string }>;
  daysWithTime: number;
  totalSeconds: number;
}

const monthFormatter = new Intl.DateTimeFormat('en-GB', { month: 'short', timeZone: 'UTC' });

export function heatmapGrid(rows: DailyTotalRow[], today: LocalDate): HeatmapGrid {
  const { from } = heatmapRange(today);
  const byDate = new Map<LocalDate, number>();
  for (const r of rows) byDate.set(r.date, (byDate.get(r.date) ?? 0) + r.seconds);

  const weeks: HeatWeek[] = [];
  const monthLabels: Array<{ index: number; label: string }> = [];
  let lastMonth = '';
  let daysWithTime = 0;
  let totalSeconds = 0;
  for (let w = weekStart(from); w <= weekStart(today); w = addDays(w, DAYS_PER_WEEK)) {
    const cells: HeatCell[] = [];
    for (let i = 0; i < DAYS_PER_WEEK; i += 1) {
      const date = addDays(w, i);
      const inWindow = date >= from && date <= today;
      const seconds = inWindow ? (byDate.get(date) ?? 0) : 0;
      if (seconds > 0) {
        daysWithTime += 1;
        totalSeconds += seconds;
      }
      cells.push({ date, seconds, bucket: heatBucket(seconds), inWindow });
    }
    // Label a column with the month of its first in-window day when that month is new.
    const firstIn = cells.find((c) => c.inWindow);
    const month = firstIn ? firstIn.date.slice(0, 7) : '';
    if (firstIn && month !== lastMonth) {
      monthLabels.push({ index: weeks.length, label: monthFormatter.format(new Date(`${firstIn.date}T00:00:00Z`)) });
      lastMonth = month;
    }
    weeks.push({ weekStart: w, cells });
  }
  return { weeks, monthLabels, daysWithTime, totalSeconds };
}
